import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Sparkles, ArrowLeft, Download, Copy, Target, Award, Lock, FileText, FileDown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, getEvaluation, improveResume } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProUnlockPaywall from "@/components/ProUnlockPaywall";

function ScoreRing({ value, label, size = 180 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = 70;
  const c = 2 * Math.PI * r;
  const offset = c - (v / 100) * c;
  const color = v >= 80 ? "#007A5E" : v >= 60 ? "#DC1C22" : v >= 40 ? "#FFBF00" : "#FF2A00";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#F4F4F5" strokeWidth="10" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth="10" fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="square"
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-extrabold text-5xl tabular-nums" style={{ color }}>{v}</div>
        <div className="overline mt-1">{label}</div>
      </div>
    </div>
  );
}

function CategoryBar({ name, score, summary }) {
  const v = Math.max(0, Math.min(100, score || 0));
  return (
    <div className="p-5 border-r border-b border-zinc-200/70" data-testid={`category-${name.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-display font-bold text-lg">{name}</span>
        <span className="font-mono text-sm tabular-nums">{v}<span className="text-zinc-400 text-xs">/100</span></span>
      </div>
      <div className="h-1 bg-zinc-100 mb-3">
        <div className="h-full bg-[#DC1C22]" style={{ width: `${v}%` }} />
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed">{summary}</p>
    </div>
  );
}

function ProGate({ feature, children }) {
  return (
    <div className="border border-dashed border-zinc-300 p-10 text-center">
      <Lock className="w-8 h-8 mx-auto text-zinc-400 mb-3" strokeWidth={1.5} />
      <h3 className="font-display font-bold text-2xl mb-2">{feature} is a Pro feature</h3>
      <p className="text-zinc-500 max-w-md mx-auto mb-6">Upgrade to ResumeLab Pro to unlock {feature.toLowerCase()}, AI rewrites, PDF export and unlimited audits.</p>
      <Link to="/pricing" className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white">
        See pricing
      </Link>
      {children}
    </div>
  );
}

export default function Results() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [improving, setImproving] = useState(false);
  const [coverLetter, setCoverLetter] = useState(null);
  const [clLoading, setClLoading] = useState(false);
  const [clCompany, setClCompany] = useState("");
  const [clBody, setClBody] = useState("");
  const [activeTab, setActiveTab] = useState("improvements");
  const isPro = !!user?.is_pro;

  useEffect(() => {
    (async () => {
      try {
        const d = await getEvaluation(id);
        setData(d);
        if (user?.is_pro) {
          try {
            const { data: cl } = await api.get(`/cover-letter/${id}`);
            if (cl) { setCoverLetter(cl); setClBody(cl.body); setClCompany(cl.company || ""); }
          } catch (err) {
            // No saved cover letter yet — that's expected for fresh audits.
            if (err?.response?.status !== 404) console.error("Cover letter fetch failed:", err);
          }
        }
      } catch (e) {
        toast.error("Could not load evaluation");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user?.is_pro]);

  const triggerImprove = async () => {
    if (!isPro) {
      // Switch to rewrites tab + scroll the inline paywall into view (instead of a hidden toast)
      setActiveTab("rewrites");
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.querySelector('[data-testid="pro-unlock-paywall"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
      });
      return;
    }
    setImproving(true);
    try {
      const d = await improveResume(id);
      setData(d);
      toast.success("AI rewrites ready");
    } catch (e) {
      toast.error("Improve failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setImproving(false);
    }
  };

  const generateCoverLetter = async () => {
    if (!isPro) return;
    setClLoading(true);
    try {
      const { data: cl } = await api.post(`/cover-letter/${id}`, { company: clCompany || null });
      setCoverLetter(cl);
      setClBody(cl.body);
      toast.success("Cover letter generated");
    } catch (e) {
      toast.error("Cover letter failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setClLoading(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  const exportPdf = () => { window.print(); };

  const [docxLoading, setDocxLoading] = useState(false);
  const [docxMenuOpen, setDocxMenuOpen] = useState(false);
  const TEMPLATE_OPTIONS = [
    { key: "modern", name: "Modern", tagline: "Red accent · Calibri" },
    { key: "classic", name: "Classic", tagline: "Cambria serif · centered" },
    { key: "tech", name: "Tech", tagline: "Consolas · green accent" },
    { key: "executive", name: "Executive", tagline: "Georgia · navy · director-level" },
    { key: "two_column", name: "Two-Column", tagline: "Sidebar + main" },
    { key: "academic", name: "Academic", tagline: "Garamond · research framing" },
  ];
  const downloadRevisedDocx = async (templateKey = "modern") => {
    if (!isPro) {
      setActiveTab("rewrites");
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.querySelector('[data-testid="pro-unlock-paywall"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
      });
      return;
    }
    setDocxMenuOpen(false);
    setDocxLoading(true);
    try {
      const res = await api.post(`/resumes/${id}/revised-docx?template=${templateKey}`, null, { responseType: "blob" });
      const disp = res.headers?.["content-disposition"] || "";
      let filename = `ResumeLab_Revised_${templateKey}.docx`;
      const m = /filename="([^"]+)"/.exec(disp);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Revised CV (${templateKey}) downloaded`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Please try again.";
      toast.error("Download failed", { description: detail });
    } finally {
      setDocxLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="results-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </main>
    );
  }
  if (!data) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 mb-4">Evaluation not found.</p>
          <Link to="/evaluate" className="btn-sharp bg-[#DC1C22] text-white">Run a new audit</Link>
        </div>
      </main>
    );
  }

  const date = new Date(data.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="results-page">
      {/* Top bar - hidden in print */}
      <div className="border-b border-zinc-200 print:hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/history" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> All audits
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs text-zinc-500 font-mono">{data.id.slice(0, 8)} · {date}</div>
            {isPro ? (
              <div className="relative">
                <button
                  onClick={() => setDocxMenuOpen((v) => !v)}
                  disabled={docxLoading}
                  data-testid="download-revised-docx"
                  className="btn-sharp bg-[#EF4444] hover:bg-[#DC2626] text-white px-3 py-1.5 text-xs disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {docxLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                  {docxLoading ? "Generating…" : "Download Revised CV (Word)"}
                </button>
                {docxMenuOpen ? (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setDocxMenuOpen(false)} aria-hidden="true" />
                    <div
                      className="absolute right-0 top-full mt-2 z-40 bg-white border border-zinc-200 shadow-xl w-72"
                      data-testid="template-picker-menu"
                    >
                      <div className="px-4 py-3 border-b border-zinc-200">
                        <div className="overline">◷ Pick a template</div>
                        <div className="text-xs text-zinc-500 mt-1">Same content, different visual style.</div>
                      </div>
                      <ul className="max-h-80 overflow-y-auto">
                        {TEMPLATE_OPTIONS.map((opt) => (
                          <li key={opt.key}>
                            <button
                              type="button"
                              onClick={() => downloadRevisedDocx(opt.key)}
                              data-testid={`template-picker-${opt.key}`}
                              className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-3 transition-colors"
                            >
                              <div>
                                <div className="font-semibold text-sm text-zinc-900">{opt.name}</div>
                                <div className="text-xs text-zinc-500 mt-0.5">{opt.tagline}</div>
                              </div>
                              <FileDown className="w-4 h-4 text-zinc-400 shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/templates"
                        className="block px-4 py-3 border-t border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors text-center font-mono uppercase tracking-widest"
                        data-testid="template-picker-browse-all"
                      >
                        Browse all templates →
                      </Link>
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <button
                onClick={() => downloadRevisedDocx()}
                data-testid="download-revised-docx-locked"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-500 border border-zinc-200 hover:bg-zinc-50"
              >
                <Lock className="w-3.5 h-3.5" /> Revised CV — Word (Pro)
              </button>
            )}
            {isPro ? (
              <button onClick={exportPdf} data-testid="export-pdf" className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white px-3 py-1.5 text-xs">
                <Download className="w-3.5 h-3.5" /> Export PDF
              </button>
            ) : (
              <Link to="/pricing" data-testid="export-pdf-locked" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-500 border border-zinc-200 hover:bg-zinc-50">
                <Lock className="w-3.5 h-3.5" /> PDF export (Pro)
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block px-6 py-4 border-b border-zinc-300">
        <div className="font-display font-extrabold text-2xl">ResumeLab · Resume Audit</div>
        <div className="text-xs text-zinc-500">{date} · {data.id}</div>
      </div>

      {/* Scoreboard */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12 print:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 flex flex-col items-center lg:items-start">
              <div className="overline mb-3">◆ RESUMELAB SCORE</div>
              <ScoreRing value={data.overall_score} label="OVERALL" size={220} />
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="px-3 py-1 border border-zinc-200 text-xs font-mono uppercase tracking-wider">
                  ATS · {data.ats_score}/100
                </span>
                {data.target_role && (
                  <span className="px-3 py-1 bg-zinc-900 text-white text-xs font-mono uppercase tracking-wider" data-testid="target-role-badge">
                    <Target className="w-3 h-3 inline mr-1" /> {data.target_role}
                  </span>
                )}
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="overline mb-3">EXECUTIVE SUMMARY</div>
              <p className="font-display text-2xl lg:text-3xl tracking-tight leading-tight text-zinc-900 mb-8" data-testid="summary-text">
                {data.summary}
              </p>
              {data.candidate_name && (
                <div className="text-sm text-zinc-500">Candidate: <span className="text-zinc-900 font-medium">{data.candidate_name}</span></div>
              )}
              <div className="mt-8 flex flex-wrap gap-3 print:hidden">
                <button
                  onClick={triggerImprove}
                  disabled={improving}
                  data-testid="improve-button"
                  className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white disabled:bg-zinc-300"
                >
                  {improving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Rewriting...</>
                  ) : (
                    <>{!isPro && <Lock className="w-4 h-4" />} <Sparkles className="w-4 h-4" /> Generate AI rewrites</>
                  )}
                </button>
                <Link to="/evaluate" className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white">
                  New audit
                </Link>
              </div>
            </div>
          </div>
          {!isPro && (
            <div className="mt-10 print:hidden">
              <ProUnlockPaywall
                variant="compact"
                improvementsCount={data.improvements?.length || 0}
              />
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="overline mb-6">◇ DIMENSION BREAKDOWN</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-zinc-200/70">
            {data.category_scores?.map((c) => (
              <CategoryBar key={c.name} name={c.name} score={c.score} summary={c.summary} />
            ))}
          </div>
        </div>
      </section>

      {/* Insights tabs */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="rounded-none bg-transparent h-auto p-0 inline-flex border border-zinc-200 overflow-x-auto flex-wrap print:hidden">
              <TabsTrigger value="improvements" data-testid="tab-improvements" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                Line-by-line ({data.improvements?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="strengths" data-testid="tab-strengths" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                Strengths
              </TabsTrigger>
              <TabsTrigger value="weaknesses" data-testid="tab-weaknesses" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                Weaknesses
              </TabsTrigger>
              <TabsTrigger value="keywords" data-testid="tab-keywords" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                Keywords
              </TabsTrigger>
              <TabsTrigger value="skills" data-testid="tab-skills" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                Skill Gaps
              </TabsTrigger>
              {data.rewritten_sections?.length > 0 ? (
                <TabsTrigger value="rewrites" data-testid="tab-rewrites" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                  AI Rewrites ({data.rewritten_sections.length})
                </TabsTrigger>
              ) : (
                <TabsTrigger value="rewrites" data-testid="tab-rewrites" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                  AI Rewrites {!isPro && <Lock className="w-3 h-3 ml-1" />}
                </TabsTrigger>
              )}
              <TabsTrigger value="cover" data-testid="tab-cover" className="rounded-none px-5 py-2.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none">
                <FileText className="w-3.5 h-3.5 mr-1" /> Cover Letter {!isPro && <Lock className="w-3 h-3 ml-1" />}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="improvements" className="mt-8 space-y-5">
              {data.improvements?.length ? data.improvements.map((imp, i) => (
                <div key={i} data-testid={`improvement-${i}`} className="border border-zinc-200 p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-2">
                    <div className="font-mono text-xs text-zinc-400">{String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-2 overline">{imp.section}</div>
                  </div>
                  <div className="lg:col-span-10">
                    {imp.original && (
                      <div className="mb-4">
                        <div className="text-xs uppercase tracking-wider text-zinc-400 mb-1">Original</div>
                        <p className="text-sm text-zinc-500 line-through decoration-zinc-300">{imp.original}</p>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs uppercase tracking-wider text-[#DC1C22]">Suggested</div>
                        <button onClick={() => copy(imp.suggestion)} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 print:hidden">
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <p className="font-medium text-zinc-900">{imp.suggestion}</p>
                    </div>
                    {imp.rationale && (
                      <p className="mt-3 text-xs text-zinc-500 italic">→ {imp.rationale}</p>
                    )}
                  </div>
                </div>
              )) : <p className="text-zinc-500">No improvements identified.</p>}
            </TabsContent>

            <TabsContent value="strengths" className="mt-8">
              <ul className="space-y-3">
                {data.strengths?.map((s, i) => (
                  <li key={i} data-testid={`strength-${i}`} className="flex items-start gap-3 border border-zinc-200 p-5">
                    <CheckCircle2 className="w-5 h-5 text-[#007A5E] mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="weaknesses" className="mt-8">
              <ul className="space-y-3">
                {data.weaknesses?.map((w, i) => (
                  <li key={i} data-testid={`weakness-${i}`} className="flex items-start gap-3 border border-zinc-200 p-5">
                    <AlertTriangle className="w-5 h-5 text-[#FF2A00] mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="keywords" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="overline mb-3 text-[#007A5E]">✓ FOUND</div>
                  <div className="flex flex-wrap gap-2">
                    {data.keyword_matches?.length ? data.keyword_matches.map((k, i) => (
                      <span key={i} data-testid={`keyword-match-${i}`} className="px-3 py-1 bg-emerald-50 text-emerald-900 text-sm border border-emerald-200">
                        {k}
                      </span>
                    )) : <p className="text-zinc-400 text-sm">None detected.</p>}
                  </div>
                </div>
                <div>
                  <div className="overline mb-3 text-[#FF2A00]">✗ MISSING</div>
                  <div className="flex flex-wrap gap-2">
                    {data.missing_keywords?.length ? data.missing_keywords.map((k, i) => (
                      <span key={i} data-testid={`keyword-missing-${i}`} className="px-3 py-1 bg-red-50 text-red-900 text-sm border border-red-200">
                        {k}
                      </span>
                    )) : <p className="text-zinc-400 text-sm">No critical gaps.</p>}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="skills" className="mt-8">
              {data.skill_gaps?.length ? (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.skill_gaps.map((s, i) => (
                    <li key={i} data-testid={`skill-gap-${i}`} className="border border-zinc-200 p-4 flex items-start gap-3">
                      <Award className="w-5 h-5 text-[#DC1C22] mt-0.5 shrink-0" strokeWidth={1.75} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-zinc-500">No critical skill gaps identified.</p>}
            </TabsContent>

            {data.rewritten_sections?.length > 0 ? (
              <TabsContent value="rewrites" className="mt-8 space-y-5">
                {data.rewritten_sections.map((r, i) => (
                  <div key={i} data-testid={`rewrite-${i}`} className="border border-zinc-200 p-6 lg:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="overline">{r.section}</div>
                      <button onClick={() => copy(r.rewritten_text)} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 print:hidden">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-zinc-900 leading-relaxed">{r.rewritten_text}</pre>
                  </div>
                ))}
              </TabsContent>
            ) : (
              <TabsContent value="rewrites" className="mt-8">
                {isPro ? (
                  <div className="border border-zinc-200 p-10 text-center">
                    <Sparkles className="w-8 h-8 mx-auto text-[#DC1C22] mb-3" strokeWidth={1.5} />
                    <h3 className="font-display font-bold text-2xl mb-2">Ready when you are.</h3>
                    <p className="text-zinc-500 max-w-md mx-auto mb-6">Click &ldquo;Generate AI rewrites&rdquo; above to produce copy-ready, recruiter-grade rewrites of every flagged section.</p>
                    <button
                      onClick={triggerImprove}
                      disabled={improving}
                      data-testid="rewrites-tab-trigger-improve"
                      className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white disabled:bg-zinc-300"
                    >
                      {improving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Rewriting...</>) : (<><Sparkles className="w-4 h-4" /> Generate AI rewrites</>)}
                    </button>
                  </div>
                ) : (
                  <ProUnlockPaywall
                    variant="inline"
                    improvementsCount={data.improvements?.length || 0}
                    sections={data.improvements?.map((i) => i.section) || []}
                  />
                )}
              </TabsContent>
            )}

            <TabsContent value="cover" className="mt-8">
              {!isPro ? (
                <ProUnlockPaywall
                  variant="inline"
                  improvementsCount={data.improvements?.length || 0}
                  sections={data.improvements?.map((i) => i.section) || []}
                />
              ) : (
                <div className="border border-zinc-200 p-6 lg:p-8" data-testid="cover-letter-pane">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mb-6">
                    <div className="md:col-span-8">
                      <label className="overline block mb-2">Company (optional)</label>
                      <input
                        type="text"
                        value={clCompany}
                        onChange={(e) => setClCompany(e.target.value)}
                        placeholder="e.g. Stripe"
                        data-testid="cover-company-input"
                        className="w-full border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <button
                        onClick={generateCoverLetter}
                        disabled={clLoading}
                        data-testid="generate-cover-letter"
                        className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white w-full"
                      >
                        {clLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Writing...</>) :
                         coverLetter ? (<><Sparkles className="w-4 h-4" /> Regenerate</>) :
                         (<><Sparkles className="w-4 h-4" /> Generate</>)}
                      </button>
                    </div>
                  </div>

                  {coverLetter ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="overline">YOUR COVER LETTER</div>
                        <button onClick={() => copy(clBody)} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <textarea
                        value={clBody}
                        onChange={(e) => setClBody(e.target.value)}
                        rows={18}
                        data-testid="cover-letter-textarea"
                        className="w-full border border-zinc-200 p-4 font-sans text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
                      />
                      <p className="text-xs text-zinc-500 mt-2">Edits stay in your browser. Click Regenerate to fetch a fresh draft from AI.</p>
                    </>
                  ) : (
                    <p className="text-zinc-500 text-sm">Click Generate to write a cover letter tailored to this resume and job description.</p>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
}
